import React, { ReactElement, useState, useEffect } from 'react'
import {
  Tooltip as MuiTooltip,
  Popover,
  Box,
  Typography,
  Divider,
} from '@mui/material'
import styled from 'styled-components'
import { I18n } from 'common'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'

interface Bundle {
  bundleId: number
  bundleName: string
  bundleDesc: string
}

interface BundleTooltipProps {
  bundles: Bundle[]
  placement?: 'bottom' | 'top' | 'left' | 'right'
}

const BundleChip = () => {
  return (
    <BundleTag>
      <span>
        <I18n name="bundleEligible" />
      </span>
      <TagIcon />
    </BundleTag>
  )
}

export const BundleTooltip = ({
  bundles = [],
  placement = 'top',
}: BundleTooltipProps): ReactElement => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget)
  }

  const handleClose = () => setAnchorEl(null)
  const open = Boolean(anchorEl)
  const isMultiple = bundles.length > 1

  const content = (
    <Box sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
      {bundles.map((bundle, index) => (
        <Box key={bundle.bundleId}>
          <Typography
            sx={{
              fontFamily: 'Oswald',
              fontWeight: 700,
              fontSize: '14px',
              lineHeight: '21px',
              textTransform: 'uppercase',
            }}
          >
            {bundle.bundleName}
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Roboto',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '21px',
              marginTop: '4px',
            }}
          >
            {bundle.bundleDesc}
          </Typography>
          {index !== bundles.length - 1 && (
            <Divider sx={{ my: 1.5, borderColor: '#ddd' }} />
          )}
        </Box>
      ))}
    </Box>
  )

  if (isMobile) {
    return (
      <>
        <HoverTarget onClick={handleClick}>
          <BundleChip />
        </HoverTarget>
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{
            sx: {
              backgroundColor: '#fff',
              color: '#000',
              border: '1px solid #000',
              borderRadius: '6px',
              maxWidth: isMultiple ? 500 : 360,
              width: '90vw',
              padding: '16px',
              boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
            },
          }}
        >
          {content}
        </Popover>
      </>
    )
  }

  return (
    <MuiTooltip
      title={content}
      arrow
      placement={placement}
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: '#fff',
            color: '#000',
            border: '1px solid #000',
            borderRadius: '6px',
            maxWidth: isMultiple ? 500 : 360,
            padding: '16px',
            boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
            '@media (max-width: 768px)': {
              maxWidth: '90vw',
              fontSize: '14px',
            },
          },
        },
        arrow: {
          sx: {
            color: '#fff',
            '&::before': {
              backgroundColor: '#fff',
              border: '1px solid #000',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            },
          },
        },
      }}
    >
      <HoverTarget>
        <BundleChip />
      </HoverTarget>
    </MuiTooltip>
  )
}

const HoverTarget = styled.span`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
`

const BundleTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background-color: #f6e8b1;
  color: #000;
  font-weight: 500;
  font-size: 14px;
  padding: 4px 10px;
  border-radius: 4px;
  cursor: default;
  width: fit-content;
  transition: all 0.2s ease;
  margin-bottom: 8px;

  &:hover {
    background-color: #000;
    color: #f6e8b1;

    svg {
      color: #f6e8b1;
    }
  }
`

const TagIcon = styled(LocalOfferIcon)`
  color: #000;
  font-size: 16px;
`
